import {
  DAY,
  DAY_CLOUDS,
  DAY_HORIZON,
  DAY_STARS,
} from './day-sky-data';
import type {HorizonSceneHandle} from './horizon-scene';
import type {
  SlideMarkBackground,
  SlideMarkDaySkyBackground,
  SlideMarkHorizonBackground,
} from './types';

const SVG_NS = 'http://www.w3.org/2000/svg';
let gradientSequence = 0;

const svgNode = <K extends keyof SVGElementTagNameMap>(
  name: K,
  attributes: Record<string, string | number> = {},
) => {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, String(value)));
  return node;
};

const addCircle = (
  target: SVGElement,
  x: number,
  y: number,
  width: number,
  height: number,
  attributes: Record<string, string | number>,
) => {
  target.append(svgNode('ellipse', {
    cx: x,
    cy: y,
    rx: width / 2,
    ry: height / 2,
    ...attributes,
  }));
};

const makeOriginalScene = (background: SlideMarkDaySkyBackground) => {
  const stage = DAY[background.stage];
  if (!stage) throw new Error(`The original createDaySky has no stage ${background.stage}`);
  const gradientId = `sm-original-day-gradient-${++gradientSequence}`;
  const svg = svgNode('svg', {
    class: 'sm-day-sky-scene',
    viewBox: '-960 -540 1920 1080',
    preserveAspectRatio: 'xMidYMid slice',
    'aria-hidden': 'true',
  });
  const definitions = svgNode('defs');
  const gradient = svgNode('linearGradient', {
    id: gradientId,
    'data-original-gradient': '',
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 1,
  });
  stage.sky.forEach((color, index) => {
    gradient.append(svgNode('stop', {
      offset: stage.stops[index],
      'stop-color': color,
    }));
  });
  definitions.append(gradient);
  svg.append(definitions);
  svg.append(svgNode('rect', {
    'data-original-sky': '',
    x: -960,
    y: -540,
    width: 1920,
    height: 1080,
    fill: `url(#${gradientId})`,
  }));

  const stars = svgNode('g', {'data-original-stars': '', opacity: stage.stars});
  DAY_STARS.forEach(([x, y, size]) =>
    addCircle(stars, x, y, size, size, {fill: '#fdf1e0'}));
  svg.append(stars);

  const halo = svgNode('ellipse', {
    'data-original-halo': '',
    cx: stage.sun.x,
    cy: stage.sun.y,
    rx: 165,
    ry: 165,
    fill: stage.sun.glow,
    opacity: 0.16,
  });
  svg.append(halo);
  const sun = svgNode('circle', {
    class: 'sm-day-sky-original-sun',
    cx: stage.sun.x,
    cy: stage.sun.y,
    r: 95,
    fill: stage.sun.fill,
    style: `--sm-original-sun-glow:${stage.sun.glow};--sm-original-sun-blur:${stage.sun.blur}px`,
  });
  svg.append(sun);

  svg.append(svgNode('rect', {
    'data-original-ground': '',
    x: -960,
    y: 315,
    width: 1920,
    height: 226,
    fill: stage.ground,
  }));

  const details = svgNode('g', {fill: stage.detail});
  details.dataset.originalDetails = '';
  details.append(svgNode('path', {
    'data-original-hangar': '',
    d: 'M-744 317 V283 Q-744 253 -714 253 H-606 Q-576 253 -576 283 V317 Z',
  }));
  details.append(svgNode('rect', {x: -718, y: 232, width: 12, height: 26}));
  const trees: readonly (readonly [number, number])[] = [
    [-430, 30], [-372, 40], [-322, 26], [812, 34], [872, 44], [926, 28],
  ];
  trees.forEach(([x, radius]) => {
    addCircle(details, x, DAY_HORIZON - radius * 0.72, radius * 2, radius * 2, {});
  });
  trees.forEach(([x, radius]) => {
    details.append(svgNode('rect', {
      x: x - 3.5,
      y: DAY_HORIZON + radius * 0.2 - radius * 0.4,
      width: 7,
      height: radius * 0.8,
    }));
  });
  svg.append(details);

  addCircle(svg, 150, DAY_HORIZON + 68, 210, 52, {
    fill: 'none',
    stroke: 'rgba(253, 241, 224, 0.28)',
    'stroke-width': 5,
  });
  addCircle(svg, 150, DAY_HORIZON + 68, 104, 26, {
    fill: 'none',
    stroke: 'rgba(253, 241, 224, 0.22)',
    'stroke-width': 4,
  });
  addCircle(svg, 150, DAY_HORIZON + 68, 24, 7, {
    fill: 'rgba(253, 241, 224, 0.3)',
  });

  svg.append(svgNode('line', {
    'data-original-windsock-pole': '',
    x1: 640,
    y1: DAY_HORIZON,
    x2: 640,
    y2: DAY_HORIZON - 64,
    stroke: stage.detail,
    'stroke-width': 6,
  }));
  svg.append(svgNode('path', {
    'data-original-windsock': '',
    d: `M640 ${DAY_HORIZON - 64} L694 ${DAY_HORIZON - 55} L640 ${DAY_HORIZON - 46} Z`,
    fill: '#ff6a3d',
    opacity: 0.85,
  }));

  const cloudLayer = svgNode('g', {
    class: 'sm-day-sky-original-clouds',
    'data-original-cloud-layer': '',
    opacity: stage.clouds,
  });
  DAY_CLOUDS.forEach((cloud, index) => {
    const motion = svgNode('g', {
      'data-original-cloud': index,
      opacity: cloud.opacity,
    });
    const duration = 2400 / cloud.speed;
    const progress = (cloud.x0 + 1200) / 2400;
    motion.append(svgNode('animateTransform', {
      'data-cloud-motion': '',
      attributeName: 'transform',
      type: 'translate',
      from: `-1200 ${cloud.y}`,
      to: `1200 ${cloud.y}`,
      dur: `${duration}s`,
      begin: `${-duration * progress}s`,
      repeatCount: 'indefinite',
    }));
    const shape = svgNode('g', {transform: `scale(${cloud.scale})`});
    shape.append(svgNode('rect', {
      'data-original-cloud-shade': '',
      x: -143,
      y: 11,
      width: 286,
      height: 58,
      rx: 29,
      fill: stage.cloudShade,
    }));
    addCircle(shape, -80, 14, 130, 130, {
      'data-original-cloud-fill': '',
      fill: stage.cloudFill,
    });
    addCircle(shape, -4, -18, 175, 175, {
      'data-original-cloud-fill': '',
      fill: stage.cloudFill,
    });
    addCircle(shape, 74, 14, 120, 120, {
      'data-original-cloud-fill': '',
      fill: stage.cloudFill,
    });
    shape.append(svgNode('rect', {
      'data-original-cloud-fill': '',
      x: -125,
      y: 6,
      width: 250,
      height: 40,
      rx: 20,
      fill: stage.cloudFill,
    }));
    motion.append(shape);
    cloudLayer.append(motion);
  });
  svg.append(cloudLayer);
  return svg;
};

const parseHex = (value: string) => {
  const hex = value.replace('#', '');
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ] as const;
};

const mixColor = (from: string, to: string, progress: number) => {
  const a = parseHex(from);
  const b = parseHex(to);
  const values = a.map((value, index) =>
    Math.round(value + (b[index] - value) * progress));
  return `rgb(${values.join(', ')})`;
};

const easeInOutCubic = (value: number) =>
  value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;

const transitionOriginalScene = (
  svg: SVGSVGElement,
  fromIndex: number,
  toIndex: number,
  durationSeconds: number,
  isCurrent: () => boolean,
) => {
  const from = DAY[fromIndex];
  const to = DAY[toIndex];
  const baseGradient = svg.querySelector<SVGLinearGradientElement>('[data-original-gradient]')!;
  const baseSky = svg.querySelector<SVGRectElement>('[data-original-sky]')!;
  const overlayId = `sm-original-day-overlay-${++gradientSequence}`;
  const overlayGradient = svgNode('linearGradient', {
    id: overlayId,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 1,
  });
  to.sky.forEach((color, index) => {
    overlayGradient.append(svgNode('stop', {
      offset: to.stops[index],
      'stop-color': color,
    }));
  });
  svg.querySelector('defs')!.append(overlayGradient);
  const overlay = svgNode('rect', {
    'data-original-sky-overlay': '',
    x: -960,
    y: -540,
    width: 1920,
    height: 1080,
    fill: `url(#${overlayId})`,
    opacity: 0,
  });
  baseSky.after(overlay);

  const stars = svg.querySelector<SVGGElement>('[data-original-stars]')!;
  const halo = svg.querySelector<SVGEllipseElement>('[data-original-halo]')!;
  const sun = svg.querySelector<SVGCircleElement>('.sm-day-sky-original-sun')!;
  const ground = svg.querySelector<SVGRectElement>('[data-original-ground]')!;
  const details = svg.querySelector<SVGGElement>('[data-original-details]')!;
  const pole = svg.querySelector<SVGLineElement>('[data-original-windsock-pole]')!;
  const cloudLayer = svg.querySelector<SVGGElement>('[data-original-cloud-layer]')!;
  const cloudFills = [...svg.querySelectorAll<SVGElement>('[data-original-cloud-fill]')];
  const cloudShades = [...svg.querySelectorAll<SVGElement>('[data-original-cloud-shade]')];
  const start = performance.now();

  return new Promise<void>(resolve => {
    const finalize = () => {
      const baseStops = [...baseGradient.querySelectorAll('stop')];
      baseStops.forEach((stop, index) => {
        stop.setAttribute('offset', String(to.stops[index]));
        stop.setAttribute('stop-color', to.sky[index]);
      });
      overlay.remove();
      overlayGradient.remove();
    };
    const setFrame = (raw: number, positionProgress: number) => {
      overlay.setAttribute('opacity', String(positionProgress));
      stars.setAttribute('opacity', String(from.stars + (to.stars - from.stars) * raw));
      cloudLayer.setAttribute(
        'opacity',
        String(from.clouds + (to.clouds - from.clouds) * raw),
      );
      const sunX = from.sun.x + (to.sun.x - from.sun.x) * positionProgress;
      const sunY = from.sun.y + (to.sun.y - from.sun.y) * positionProgress;
      sun.setAttribute('cx', String(sunX));
      sun.setAttribute('cy', String(sunY));
      sun.setAttribute('fill', mixColor(from.sun.fill, to.sun.fill, raw));
      sun.style.setProperty(
        '--sm-original-sun-glow',
        mixColor(from.sun.glow, to.sun.glow, raw),
      );
      sun.style.setProperty(
        '--sm-original-sun-blur',
        `${from.sun.blur + (to.sun.blur - from.sun.blur) * raw}px`,
      );
      halo.setAttribute('cx', String(sunX));
      halo.setAttribute('cy', String(sunY));
      halo.setAttribute('fill', mixColor(from.sun.glow, to.sun.glow, raw));
      ground.setAttribute('fill', mixColor(from.ground, to.ground, raw));
      const detail = mixColor(from.detail, to.detail, raw);
      details.setAttribute('fill', detail);
      pole.setAttribute('stroke', detail);
      const cloudFill = mixColor(from.cloudFill, to.cloudFill, raw);
      const cloudShade = mixColor(from.cloudShade, to.cloudShade, raw);
      cloudFills.forEach(node => node.setAttribute('fill', cloudFill));
      cloudShades.forEach(node => node.setAttribute('fill', cloudShade));
    };
    const draw = (now: number) => {
      if (!isCurrent()) {
        // An interrupted transition must land on its target state, never
        // freeze mid-interpolation with the base gradient still on "from".
        setFrame(1, 1);
        finalize();
        resolve();
        return;
      }
      const raw = Math.min(1, (now - start) / (durationSeconds * 1000));
      setFrame(raw, easeInOutCubic(raw));
      if (raw < 1) {
        requestAnimationFrame(draw);
        return;
      }
      finalize();
      resolve();
    };
    requestAnimationFrame(draw);
  });
};

export interface SlideMarkBackgroundPlayer {
  attach(target: HTMLElement, background: SlideMarkBackground | undefined): Promise<void>;
  unmount(): void;
}

/** One renderer per background kind; the shared player composes them. */
interface SlideMarkKindPlayer {
  readonly layer: HTMLElement;
  attach(target: HTMLElement, background: SlideMarkBackground): Promise<void>;
  unmount(): void;
}

const stampLayer = (layer: HTMLElement, background: SlideMarkBackground) => {
  layer.dataset.background = background.id;
  layer.dataset.animation = background.animation;
  layer.dataset.source = background.source;
  layer.dataset.stage = String(background.stage);
  layer.dataset.transition = String(background.transition);
};

const makeBackgroundLayer = (kind: SlideMarkBackground['kind']) => {
  const layer = document.createElement('div');
  layer.className = `sm-background sm-background-${kind}`;
  layer.setAttribute('aria-hidden', 'true');
  return layer;
};

/**
 * Faithful DOM translation of the original Motion Canvas createDaySky.
 * All values come from the shared canonical data; this renderer adds no
 * scenery or motion of its own.
 */
const createDaySkyPlayer = (): SlideMarkKindPlayer => {
  const layer = makeBackgroundLayer('day-sky');
  let current: SlideMarkDaySkyBackground | undefined;
  let scene: SVGSVGElement | undefined;
  let updateSequence = 0;

  return {
    layer,
    async attach(target, incoming) {
      if (incoming.kind !== 'day-sky') throw new Error('Day-sky player received a non-day-sky background');
      const background = incoming;
      const sequence = ++updateSequence;
      target.prepend(layer);
      stampLayer(layer, background);
      if (!current || !scene || current.animation !== background.animation) {
        scene = makeOriginalScene(background);
        layer.replaceChildren(scene);
        current = background;
        return;
      }
      if (current.stage === background.stage) {
        current = background;
        return;
      }
      if (background.transition === 'cut') {
        scene = makeOriginalScene(background);
        layer.replaceChildren(scene);
        current = background;
        return;
      }
      const previous = current;
      current = background;
      await transitionOriginalScene(
        scene,
        previous.stage,
        background.stage,
        background.transition,
        () => sequence === updateSequence,
      );
      if (sequence !== updateSequence) return;
      layer.dataset.stage = String(background.stage);
    },
    unmount() {
      updateSequence++;
      layer.remove();
      current = undefined;
      scene = undefined;
    },
  };
};

const createHorizonPlayer = (): SlideMarkKindPlayer => {
  const layer = makeBackgroundLayer('horizon');
  let scene: HorizonSceneHandle | undefined;
  let loading: Promise<typeof import('./horizon-scene')> | undefined;
  let current: SlideMarkHorizonBackground | undefined;
  let updateSequence = 0;

  return {
    layer,
    async attach(target, incoming) {
      if (incoming.kind !== 'horizon') throw new Error('Horizon player received a non-horizon background');
      const background = incoming;
      const sequence = ++updateSequence;
      target.prepend(layer);
      stampLayer(layer, background);
      if (!scene) {
        // The three.js chunk loads on first use so decks without a horizon
        // background never download it.
        loading ??= import('./horizon-scene');
        const module = await loading;
        if (sequence !== updateSequence) return;
        scene = module.createHorizonScene(layer);
        scene.setStage(background.stage);
        current = background;
        return;
      }
      if (current?.stage === background.stage) {
        current = background;
        return;
      }
      if (background.transition === 'cut') {
        scene.setStage(background.stage);
        current = background;
        return;
      }
      current = background;
      await scene.transitionTo(
        background.stage,
        background.transition,
        () => sequence === updateSequence,
      );
      if (sequence !== updateSequence) return;
      layer.dataset.stage = String(background.stage);
    },
    unmount() {
      updateSequence++;
      layer.remove();
      scene?.dispose();
      scene = undefined;
      loading = undefined;
      current = undefined;
    },
  };
};

/* Total mapping: a new background kind must register a player here or the
   compiler refuses the union. */
const KIND_PLAYER_FACTORIES: Record<SlideMarkBackground['kind'], () => SlideMarkKindPlayer> = {
  'day-sky': createDaySkyPlayer,
  horizon: createHorizonPlayer,
};

export const createSlideMarkBackgroundPlayer = (): SlideMarkBackgroundPlayer => {
  let active: {kind: SlideMarkBackground['kind']; player: SlideMarkKindPlayer} | undefined;
  let fadeCleanup: (() => void) | undefined;
  let updateSequence = 0;

  const finishFade = () => {
    fadeCleanup?.();
    fadeCleanup = undefined;
  };

  const crossfadeOut = (player: SlideMarkKindPlayer, seconds: number, anchor: HTMLElement) => {
    // The retiring kind keeps painting above the incoming one while it fades.
    anchor.after(player.layer);
    player.layer.style.transition = `opacity ${seconds}s ease`;
    const timer = window.setTimeout(() => {
      fadeCleanup = undefined;
      player.unmount();
      player.layer.style.removeProperty('transition');
      player.layer.style.removeProperty('opacity');
    }, seconds * 1000);
    fadeCleanup = () => {
      window.clearTimeout(timer);
      player.unmount();
      player.layer.style.removeProperty('transition');
      player.layer.style.removeProperty('opacity');
    };
    requestAnimationFrame(() => {
      player.layer.style.opacity = '0';
    });
  };

  return {
    async attach(target, background) {
      const sequence = ++updateSequence;
      if (!background) {
        finishFade();
        active?.player.unmount();
        active = undefined;
        return;
      }
      if (active && active.kind !== background.kind) {
        finishFade();
        const previous = active.player;
        const next = KIND_PLAYER_FACTORIES[background.kind]();
        active = {kind: background.kind, player: next};
        await next.attach(target, background);
        if (sequence !== updateSequence) {
          previous.unmount();
          return;
        }
        const seconds = typeof background.transition === 'number' ? background.transition : 0;
        if (seconds <= 0) previous.unmount();
        else crossfadeOut(previous, seconds, next.layer);
        return;
      }
      active ??= {kind: background.kind, player: KIND_PLAYER_FACTORIES[background.kind]()};
      await active.player.attach(target, background);
    },
    unmount() {
      updateSequence++;
      finishFade();
      active?.player.unmount();
      active = undefined;
    },
  };
};

export const mountSlideMarkBackground = (
  target: HTMLElement,
  background: SlideMarkBackground | undefined,
) => {
  const player = createSlideMarkBackgroundPlayer();
  void player.attach(target, background);
  return player;
};
